<template>
  <section class="section content-container">
    <n-loading-spinner v-if="isLoading"></n-loading-spinner>
    <div class="container is-fluid" v-if="!isLoading">
      <div class="island">
        <nav class="level">
          <div class="level-left"></div>
          <div class="level-right">
            <p class="level-item">
              <n-button @click="openInviteUserModal" variant="primary">
                <span>Invite new {{ userTerm }}</span>
              </n-button>
            </p>
          </div>
        </nav>

        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>

          <tr v-for="user in users" :key="user.id">
            <td>{{ user.displayName }}</td>
            <td>{{ user.userId }}</td>
            <td>{{ formatDate(user.createdTimeUtc) }}</td>
            <td>
              <span class="tag" :class="getStatusClass(user.status)">{{ getStatus(user.status) }}</span>
            </td>
            <td>
              <span @click="openRemoveUserModal(user)" class="icon is-small">
                <font-awesome-icon icon="trash"></font-awesome-icon>
              </span>
            </td>
          </tr>
        </table>
      </div>
    </div>
    <ConfirmationModal
      v-if="showRemoveUserModal"
      confirmationTitle="Remove teammate"
      :confirmationMessage="getConfirmationMessage()"
      v-on:confirm="removeUser()"
      v-on:cancel="closeRemoveUserModal()"
    ></ConfirmationModal>

    <InviteUser
      v-if="showInviteUserModal"
      :workspace="workspace"
      :user-term="userTerm"
      @close="closeInviteUsersModal"
    ></InviteUser>
  </section>
</template>

<script lang="ts" setup>
import { workspacesApi } from '@/workspaces/workspaces';
import { UserViewModel } from '@/workspaces/models/userViewModel';
import moment from 'moment';
import InviteUser from '@/features/settings/components/InviteUser.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Toast from '@/shared/services/toast';
import { computed, ref, onBeforeMount, PropType } from 'vue';
import { useStore } from 'vuex';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';

const props = defineProps({
  providedWorkspace: {
    type: Object as PropType<WorkspaceViewModel>,
    required: false,
  },
});

const store = useStore();
const workspace = computed(() => props.providedWorkspace ?? store.getters.currentWorkspace);
const userTerm = computed(() => (props.providedWorkspace ? 'users' : 'teammates'));

const isLoading = ref(false);
const users = ref<UserViewModel[]>([]);
const userToRemove = ref<UserViewModel>();
const showInviteUserModal = ref(false);
const showRemoveUserModal = ref(false);

onBeforeMount(async () => await getAllUsersForWorkspace());

function getStatus(status: number): string {
  if (status === 0) {
    return 'Pending';
  } else if (status === 1) {
    return 'Active';
  }
  return '';
}

function getStatusClass(status: number): string {
  if (status === 0) {
    return 'is-warning';
  } else if (status === 1) {
    return 'is-success';
  }
  return 'is-danger';
}

function formatDate(value: string): string {
  return moment(String(value)).format('DD MMM YYYY');
}

function openRemoveUserModal(user: UserViewModel): void {
  userToRemove.value = user;
  showRemoveUserModal.value = true;
}

function closeRemoveUserModal(): void {
  showRemoveUserModal.value = false;
}

function removeUser(): void {
  if (!userToRemove.value) return;

  workspacesApi
    .revokeUser(userToRemove.value.id, workspace.value.id)
    .then(() => {
      Toast.showSuccess(`Successfully removed teammate ${userToRemove.value!.userId}`);
    })
    .catch((err) => Toast.showError(err.message))
    .finally(() => {
      getAllUsersForWorkspace();
      closeRemoveUserModal();
    });
}

function getConfirmationMessage(): string {
  return `Are you sure you want to remove ${userToRemove.value!.userId} from ${workspace.value.name}?
  <br/><br/>
  Note that this will only remove their access to this workspace.`;
}

function openInviteUserModal() {
  showInviteUserModal.value = true;
}

function closeInviteUsersModal(refreshRequired: boolean) {
  if (refreshRequired) {
    getAllUsersForWorkspace();
  }
  showInviteUserModal.value = false;
}

async function getAllUsersForWorkspace() {
  isLoading.value = true;
  showRemoveUserModal.value = false;
  showInviteUserModal.value = false;
  users.value = await workspacesApi.getAllUsersForWorkspace(workspace.value.id);
  isLoading.value = false;
}
</script>
<style scoped lang="scss">
.icon {
  cursor: pointer;
}
</style>
